import React, { useState } from 'react';

import Modal from 'components/shared/Modal/Modal';
import Button from 'components/shared/Button/Button';
import Typography from 'components/shared/Typography/Typography';
import ServiceForm from 'components/shared/NewServiceModal/components/ServiceForm';
import Breadcrumb, { BreadcrumbList } from 'components/shared/Breadcrumb/Breadcrumb';

import { buildFooterActions } from './lib/helpers';
import ServicesTable from './components/ServicesTable';
import { addServicesModalColumns } from './lib/columns';
import PriceAdjustments from './components/PriceAdjustments';
import { useServiceFormStep } from './hooks/useServiceFormStep';
import { useAddServicesModal } from './hooks/useAddServicesModal';

const AddServicesModalContent = ({
  isOpen,
  onClose,
  practice,
  practiceId,
  onServicesAdded,
  isAdminContext
}) => {
  const isDental = practice?.kind === 'dental';
  const [columnDefs, setColumnDefs] = useState(() => addServicesModalColumns(isDental));

  const {
    selectedRows,
    standardChargeMultiplier,
    allowableMultiplier,
    selfPayMultiplier,
    selectedSpecialty,
    page,
    limit,
    filters,
    tabsData,
    paginatedAndFilteredData,
    totalItems,
    showControls,
    isLoading,
    isFetching,
    isError,
    setSelectedSpecialty,
    setPage,
    setLimit,
    setLocalEdits,
    changeFilters,
    handleStandardChargeChange,
    handleAllowableChange,
    handleSelfPayChange,
    handleAddServices,
    handleCreateNewService,
    onSelectionChanged,
    removeCode,
    retry
  } = useAddServicesModal({ practice, practiceId, onServicesAdded, onClose, isOpen });

  const {
    serviceFormRef,
    isFormChanged,
    setIsFormChanged,
    isCreateStep,
    isEditStep,
    formService,
    handleFormSave,
    submitForm,
    startCreateStep,
    handleEditRow,
    goBack
  } = useServiceFormStep({ setLocalEdits, handleCreateNewService });

  const handleColumnChange = (updatedColumns) => {
    setColumnDefs((prev) =>
      typeof updatedColumns === 'function' ? updatedColumns(prev) : updatedColumns
    );
  };

  const footerActions = buildFooterActions({
    isCreateStep,
    isEditStep,
    isFormChanged,
    isLoading,
    selectedRows,
    onGoBack: goBack,
    onSubmitForm: submitForm,
    onAddServices: handleAddServices
  });

  return (
    <Modal
      isOpen={isOpen}
      handleClose={onClose}
      width="large"
      customHeader={
        <div className="flex w-full items-center justify-between gap-2">
          <BreadcrumbList />
          {!isCreateStep && !isEditStep && (
            <Button
              text="Create new service"
              variant="link"
              color="neutral"
              iconSize={16}
              size="xs"
              shade={700}
              className="whitespace-nowrap text-sm"
              icon="add-square-mono"
              iconColor="neutral"
              onClick={startCreateStep}
            />
          )}
        </div>
      }
      footerActions={footerActions}>
      {isCreateStep || isEditStep ? (
        <div className="flex flex-1 justify-center">
          <ServiceForm
            ref={serviceFormRef}
            service={formService}
            isAdminContext={isAdminContext}
            practice={practice}
            onSave={handleFormSave}
            onDirtyChange={setIsFormChanged}
          />
        </div>
      ) : (
        <div className="flex h-full flex-col justify-between">
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="mb-4 flex flex-shrink-0 flex-col gap-1">
              <Typography weight="semibold" className="text-neutral-900">
                Choose services to add
              </Typography>
              <Typography className="text-sm text-neutral-600">
                Browse all services or use predefined categories to quickly add the ones relevant to
                your practice.
              </Typography>
            </div>

            <ServicesTable
              columnDefs={columnDefs}
              tabsData={tabsData}
              selectedSpecialty={selectedSpecialty}
              onSpecialtyChange={setSelectedSpecialty}
              filters={filters}
              onFilterChange={changeFilters}
              showControls={showControls}
              onColumnChange={handleColumnChange}
              onColumnDragEnd={handleColumnChange}
              paginatedAndFilteredData={paginatedAndFilteredData}
              onSelectionChanged={onSelectionChanged}
              page={page}
              limit={limit}
              totalItems={totalItems}
              onPageChange={setPage}
              onLimitChange={setLimit}
              onRemove={removeCode}
              onEditRow={handleEditRow}
              isLoading={isFetching}
              isError={isError}
              onRetry={retry}
            />
          </div>

          <div className="my-6 h-px w-full bg-neutral-200" />

          <PriceAdjustments
            standardChargeMultiplier={standardChargeMultiplier}
            allowableMultiplier={allowableMultiplier}
            selfPayMultiplier={selfPayMultiplier}
            onStandardChargeChange={handleStandardChargeChange}
            onAllowableChange={handleAllowableChange}
            onSelfPayChange={handleSelfPayChange}
            isDental={isDental}
          />
        </div>
      )}
    </Modal>
  );
};

const AddServicesModal = ({
  isOpen,
  onClose,
  practice,
  practiceId,
  onServicesAdded = () => {},
  isAdminContext = false
}) => (
  <Breadcrumb steps={[{ name: 'add-services', title: 'Add services' }]}>
    <AddServicesModalContent
      isOpen={isOpen}
      onClose={onClose}
      practice={practice}
      practiceId={practiceId}
      onServicesAdded={onServicesAdded}
      isAdminContext={isAdminContext}
    />
  </Breadcrumb>
);

export default AddServicesModal;
